/**
 * Telegram Notification Service
 * Automatically sends notifications for user activities
 */

import { APP_CONFIG } from '../config';

const TELEGRAM_API = APP_CONFIG.telegramApiUrl || 'https://alphameta.tech/api/telegram.php';

// Activity type messages
const ACTIVITY_MESSAGES = {
  investment: (data) => ({
    title: '💎 New Investment',
    emoji: '💎',
    details: [
      `Amount: $${parseFloat(data.amount).toFixed(2)} USDT`,
      `AI Bot: ${data.botName || 'Assigned'}`,
      `Daily Profit: 0.5% - 1%`,
      `Max Return: 300%`
    ]
  }),
  
  startTrade: (data) => ({
    title: '🚀 Trading Started',
    emoji: '🚀',
    details: [
      `Investment: $${parseFloat(data.amount).toFixed(2)} USDT`,
      `AI Bot: ${data.botName || 'Active'}`,
      `Status: AI Trading Active`,
      `Next Profit: ~24 hours`
    ]
  }),
  
  takeProfit: (data) => ({
    title: '💰 Profit Claimed',
    emoji: '💰',
    details: [
      `Profit: +$${parseFloat(data.profit).toFixed(2)} USDT`,
      `Rate: ${data.rate || '0.5-1'}%`,
      `Investment: $${parseFloat(data.amount).toFixed(2)} USDT`,
      `Total Earned: $${parseFloat(data.totalEarned).toFixed(2)} USDT`
    ]
  }),
  
  withdraw: (data) => ({
    title: '🏧 Withdrawal Successful',
    emoji: '🏧',
    details: [
      `Amount: $${parseFloat(data.amount).toFixed(2)} USDT`,
      `Fee (10%): $${parseFloat(data.fee).toFixed(2)} USDT`,
      `Received: $${parseFloat(data.received).toFixed(2)} USDT`,
      `Status: ✅ Completed`
    ]
  }),
  
  referral: (data) => ({
    title: '👥 New Referral',
    emoji: '👥',
    details: [
      `New Member Joined!`,
      `Referral ID: ${data.referralId || 'New User'}`,
      `Your Commission: 5%`,
      `Total Referrals: ${data.totalReferrals || 1}`
    ]
  }),
  
  referralBonus: (data) => ({
    title: '🎁 Referral Bonus',
    emoji: '🎁',
    details: [
      `Bonus: +$${parseFloat(data.amount).toFixed(2)} USDT`,
      `From: User ${data.fromUserId || 'Referral'}`,
      `Type: ${data.bonusType || 'Direct Referral'}`,
      `Status: Added to pending rewards`
    ]
  }),
  
  levelIncome: (data) => ({
    title: '🎯 Level Income',
    emoji: '🎯',
    details: [
      `Income: +$${parseFloat(data.amount).toFixed(2)} USDT`,
      `Level: ${data.level || 1}`,
      `From: User Activity`,
      `Status: Added to pending rewards`
    ]
  }),
  
  clubReward: (data) => ({
    title: '🏆 Club Reward',
    emoji: '🏆',
    details: [
      `Reward: +$${parseFloat(data.amount).toFixed(2)} USDT`,
      `Club Level: ${data.clubLevel || 1}`,
      `Achievement Unlocked!`,
      `Status: Added to pending rewards`
    ]
  })
};

/**
 * Format activity message for Telegram
 */
function formatActivityMessage(type, data, userId) {
  const activity = ACTIVITY_MESSAGES[type];
  if (!activity) return null;
  
  const msg = activity(data);
  const timestamp = new Date().toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  
  let message = `${msg.emoji} *${msg.title}*\n\n`;
  message += `👤 User ID: \`${userId}\`\n\n`;
  
  msg.details.forEach(detail => {
    message += `• ${detail}\n`;
  });
  
  message += `\n📅 ${timestamp}\n`;
  message += `\n---\n_AlphaMeta AI Trading_`;
  
  return message;
}

/**
 * Send Telegram notification
 */
export async function sendTelegramNotification(type, data, userId) {
  try {
    // Check if user has Telegram linked
    const chatId = localStorage.getItem('alphameta_telegram_id');
    if (!chatId) {
      console.log('Telegram not linked, skipping notification');
      return false;
    }
    
    // Format the message
    const message = formatActivityMessage(type, data, userId);
    if (!message) {
      console.log('Unknown activity type:', type);
      return false;
    }
    
    // Send to Telegram via backend
    const response = await fetch(TELEGRAM_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chat_id: chatId,
        message: message,
        parse_mode: 'Markdown'
      })
    });
    
    const result = await response.json();
    
    if (result.success) {
      console.log('Telegram notification sent:', type);
      return true;
    } else {
      console.error('Telegram notification failed:', result.message);
      return false;
    }
  } catch (error) {
    console.error('Error sending Telegram notification:', error);
    return false;
  }
}

/**
 * Check if Telegram is linked
 */
export function isTelegramLinked() {
  return !!localStorage.getItem('alphameta_telegram_id');
}

/**
 * Get Telegram chat ID
 */
export function getTelegramChatId() {
  return localStorage.getItem('alphameta_telegram_id');
}

export default sendTelegramNotification;
